import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { blogPosts } from '../../../mocks/blog-posts';
import ArticleContent from './components/ArticleContent';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';

const TAG_COLORS: Record<string, string> = {
  Software: 'text-teal-600 bg-teal-50',
  SaaS: 'text-teal-600 bg-teal-50',
  AI: 'text-teal-600 bg-teal-50',
  Guides: 'text-gray-600 bg-gray-100',
  Comparisons: 'text-violet-600 bg-violet-50',
  Marketing: 'text-orange-600 bg-orange-50',
  SEO: 'text-green-700 bg-green-50',
  Hosting: 'text-sky-600 bg-sky-50',
  Productivity: 'text-purple-600 bg-purple-50',
  Design: 'text-orange-600 bg-orange-50',
  'Social Media': 'text-pink-600 bg-pink-50',
  CRM: 'text-teal-600 bg-teal-50',
  Video: 'text-red-600 bg-red-50',
  'E-commerce': 'text-teal-600 bg-teal-50',
  'Project Management': 'text-indigo-600 bg-indigo-50',
};

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    if (!post) navigate('/blog');
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post, navigate]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!post) return null;

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
  const related = blogPosts.filter((p) => p.slug !== slug && (p.category === post.category || p.tags.some((t) => post.tags.includes(t)))).slice(0, 3);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.desc,
        url: `${siteUrl}/blog/${post.slug}`,
        datePublished: post.date,
        dateModified: post.date,
        author: {
          '@type': 'Person',
          name: post.author,
          jobTitle: post.authorTitle,
        },
        publisher: {
          '@type': 'Organization',
          name: '247digitrend',
          url: siteUrl,
        },
        image: {
          '@type': 'ImageObject',
          url: post.img,
        },
        keywords: post.tags.join(', '),
        articleSection: post.category,
        isPartOf: { '@type': 'Blog', name: '247digitrend Blog', url: `${siteUrl}/blog` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
          { '@type': 'ListItem', position: 3, name: post.title, item: `${siteUrl}/blog/${post.slug}` },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title={`${post.title} | 247digitrend`}
        description={post.desc.substring(0, 160)}
        canonical={`${siteUrl}/blog/${post.slug}`}
        keywords={post.tags.join(', ')}
        ogType="article"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Home</Link>
            <Link to="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Categories</Link>
            <Link to="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Reviews</Link>
            <Link to="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors">Comparisons</Link>
            <Link to="/blog" className="text-[19px] font-semibold text-violet-600">Blog</Link>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm font-medium text-gray-600 hover:text-violet-600 transition-colors flex items-center gap-1.5 whitespace-nowrap">
              <i className="ri-arrow-left-line text-base"></i> All Articles
            </Link>
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-36 pb-0">
        <div className="max-w-6xl mx-auto px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-5">
            <Link to="/" className="hover:text-violet-600 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/blog" className="hover:text-violet-600 transition-colors">Blog</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-gray-600 font-medium line-clamp-1 max-w-xs">{post.title}</span>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {post.tags.map((tag, i) => (
              <span key={i} className={`text-xs font-semibold px-3 py-1 rounded-full ${TAG_COLORS[tag] ?? 'text-gray-600 bg-gray-100'}`}>{tag}</span>
            ))}
            <span className="text-xs text-gray-400 flex items-center gap-1 ml-2">
              <i className="ri-time-line"></i> {post.read}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl font-black text-gray-900 leading-tight mb-5 max-w-4xl">{post.title}</h1>

          {/* Author + meta */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-violet-100">
              <img src={post.avatarUrl} alt={post.author} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">{post.author}</p>
              <p className="text-xs text-gray-500">{post.authorTitle}</p>
            </div>
            <div className="h-8 w-px bg-gray-200 mx-1"></div>
            <span className="text-xs text-gray-400 flex items-center gap-1.5">
              <i className="ri-calendar-line"></i> {post.date}
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1.5">
              <i className="ri-eye-line"></i> {Math.floor(Math.random() * 20 + 5)}.{Math.floor(Math.random() * 9)}K views
            </span>
          </div>
        </div>

        {/* Hero image */}
        <div className="max-w-6xl mx-auto px-8">
          <div className="w-full h-80 rounded-2xl overflow-hidden">
            <img src={post.img} alt={post.title} className="w-full h-full object-cover object-top" />
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex flex-col lg:flex-row gap-10">

            {/* LEFT: Article */}
            <div className="flex-1 min-w-0">
              <ArticleContent sections={post.sections} />

              {/* Author Bio */}
              <div className="mt-12 p-6 rounded-2xl border border-gray-100 bg-gray-50 flex items-start gap-5">
                <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-violet-100">
                  <img src={post.avatarUrl} alt={post.author} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="text-xs font-bold text-violet-600 uppercase tracking-wider mb-1">Written by</p>
                  <p className="text-base font-black text-gray-900 mb-0.5">{post.author}</p>
                  <p className="text-xs text-gray-500 font-medium mb-2">{post.authorTitle}</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{post.authorBio}</p>
                </div>
              </div>

              {/* Related Articles */}
              {related.length > 0 && (
                <div className="mt-12">
                  <h3 className="text-lg font-black text-gray-900 mb-5 flex items-center gap-2">
                    <i className="ri-article-line text-violet-500"></i> Related Articles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {related.map((r, i) => (
                      <Link key={i} to={`/blog/${r.slug}`} className="group block rounded-xl border border-gray-100 overflow-hidden hover:border-violet-200 transition-all cursor-pointer">
                        <div className="w-full h-32 overflow-hidden">
                          <img src={r.img} alt={r.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                              <img src={r.avatarUrl} alt={r.author} className="w-full h-full object-cover" />
                            </div>
                            <span className="text-xs text-gray-500">{r.author}</span>
                          </div>
                          <p className="text-xs font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-violet-700 transition-colors">{r.title}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: Sidebar */}
            <div className="lg:w-72 flex-shrink-0">
              <div className="sticky top-36 space-y-5">

                {/* Share */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-4">Share This Article</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { icon: 'ri-twitter-x-line', label: 'Twitter', color: 'bg-gray-900 text-white' },
                      { icon: 'ri-linkedin-line', label: 'LinkedIn', color: 'bg-sky-600 text-white' },
                      { icon: 'ri-facebook-line', label: 'Facebook', color: 'bg-indigo-600 text-white' },
                      { icon: 'ri-link-m', label: 'Copy Link', color: 'bg-gray-100 text-gray-700' },
                    ].map((s, i) => (
                      <button key={i} className={`flex items-center justify-center gap-2 py-2 rounded-xl text-xs font-semibold cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap ${s.color}`}>
                        <i className={`${s.icon} text-sm`}></i> {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Table of Contents */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-3 flex items-center gap-2">
                    <i className="ri-list-check text-violet-500"></i> Contents
                  </h3>
                  <ol className="space-y-1.5">
                    {post.sections
                      .filter((s) => s.type === 'h2')
                      .map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-gray-600 leading-tight">
                          <span className="text-violet-400 font-bold flex-shrink-0">{i + 1}.</span>
                          <span className="hover:text-violet-600 transition-colors cursor-pointer">{s.heading}</span>
                        </li>
                      ))}
                  </ol>
                </div>

                {/* More from author */}
                <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                  <h3 className="text-sm font-black text-gray-900 mb-3">More Articles</h3>
                  <div className="space-y-3">
                    {blogPosts
                      .filter((p) => p.slug !== slug)
                      .slice(0, 5)
                      .map((p, i) => (
                        <Link key={i} to={`/blog/${p.slug}`} className="flex items-start gap-2.5 group cursor-pointer">
                          <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={p.img} alt={p.title} className="w-full h-full object-cover object-top" />
                          </div>
                          <p className="text-xs text-gray-600 leading-tight line-clamp-2 group-hover:text-violet-600 transition-colors">{p.title}</p>
                        </Link>
                      ))}
                    <Link to="/blog" className="flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors mt-1 cursor-pointer">
                      All articles <i className="ri-arrow-right-line text-xs"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: '#f9f5ff' }} className="pt-14 pb-6 mt-10">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-10 pb-10 border-b border-gray-200">
            <div className="md:col-span-4">
              <div className="flex items-center mb-4">
                <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">Expert reviews, guides, and comparisons to help you make smarter software decisions.</p>
            </div>
            <div className="md:col-span-2 md:col-start-6">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Resources</h5>
              {[{ label: 'Reviews', href: '/reviews' }, { label: 'Comparisons', href: '/comparisons' }, { label: 'Categories', href: '/categories' }, { label: 'Blog', href: '/blog' }].map((item, i) => (
                <Link key={i} to={item.href} className="block text-sm text-gray-500 hover:text-violet-600 transition-colors mb-3">{item.label}</Link>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Company</h5>
              {[{ label: 'About Us', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Submit a Tool', href: '/submit-tool' }, { label: 'Advertise', href: '/advertise' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-violet-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Follow Us</h5>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-instagram-line'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-violet-600 hover:border-violet-300 transition-all cursor-pointer">
                    <i className={`${icon} text-base`}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">© 2026 247digitrend. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-violet-600 transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
